import type { ReportStatistics, ReportWeighting } from '../../models/end-client-reporting-package';

/**
 * The arithmetic behind a crosstab's significance marks, kept apart from the aggregation so it can be
 * read and tested on its own. Nothing here counts respondents: it takes figures `report-aggregation`
 * has already worked out and says whether two of them differ by more than chance would explain.
 */

export const DEFAULT_CONFIDENCE_LEVEL = 95;
export const DEFAULT_MIN_BASE = 30;
export const DEFAULT_CAUTION_BASE = 50;
export const DEFAULT_TABLE_DECIMALS = 0;

/** Two-tailed critical values of the normal distribution. */
const Z_CRITICAL: Record<90 | 95 | 99, number> = { 90: 1.6449, 95: 1.96, 99: 2.5758 };

/** The report's statistics with every default filled in, so callers never repeat them. */
export interface ResolvedStatistics {
  /** Null when the deck is unweighted, including a weighting set up with nothing in it yet. */
  weighting: ReportWeighting | null;
  confidenceLevel: 90 | 95 | 99;
  /** Hard threshold: below it a column is greyed, untested, and cannot be drilled into. */
  minBase: number;
  /** Soft threshold: below it a column carries a caution marker. Never below `minBase`. */
  cautionBase: number;
  /** Cells counting fewer respondents than this are suppressed. 0 is off. */
  minCellCount: number;
  correction: Correction;
  decimals: 0 | 1 | 2;
}

function usableWeighting(weighting?: ReportWeighting): ReportWeighting | null {
  if (!weighting) return null;
  if (weighting.method === 'column') return weighting.column?.trim() ? weighting : null;
  return weighting.targets?.length ? weighting : null;
}

export function resolveStatistics(statistics?: ReportStatistics): ResolvedStatistics {
  const level = statistics?.confidenceLevel;
  const decimals = statistics?.decimals;
  const minBase = statistics?.minBase !== undefined && statistics.minBase >= 0
    ? statistics.minBase
    : DEFAULT_MIN_BASE;
  const caution = statistics?.cautionBase !== undefined && statistics.cautionBase >= 0
    ? statistics.cautionBase
    : DEFAULT_CAUTION_BASE;
  const correction = statistics?.correction;
  return {
    weighting: usableWeighting(statistics?.weighting),
    confidenceLevel: level === 90 || level === 99 ? level : DEFAULT_CONFIDENCE_LEVEL,
    minBase,
    cautionBase: Math.max(minBase, caution),
    minCellCount: statistics?.minCellCount && statistics.minCellCount > 0 ? statistics.minCellCount : 0,
    correction: correction === 'bonferroni' || correction === 'bh' ? correction : 'none',
    decimals: decimals === 1 || decimals === 2 ? decimals : DEFAULT_TABLE_DECIMALS,
  };
}

export type Direction = 'higher' | 'lower' | null;

/**
 * The z statistic for proportion `p1` against `p2`, as a pooled two-sample test. Proportions are 0-1,
 * and the bases are *effective* bases: weighting makes a sample behave like a smaller one, and
 * testing a weighted figure against its raw base would call differences significant that are not.
 * Null where there is nothing to test.
 */
export function proportionZ(p1: number, n1: number, p2: number, n2: number): number | null {
  if (n1 <= 0 || n2 <= 0) return null;
  const pooled = (p1 * n1 + p2 * n2) / (n1 + n2);
  const se = Math.sqrt(pooled * (1 - pooled) * (1 / n1 + 1 / n2));
  return se > 0 ? (p1 - p2) / se : null;
}

/**
 * The z statistic for two means, as a test on independent samples. A t-test would be stricter for
 * small bases, but nothing under the report's minimum base is tested at all, and above thirty the two
 * agree to the second decimal.
 */
export function meanZ(a: MeanStats, b: MeanStats): number | null {
  if (a.effectiveBase <= 1 || b.effectiveBase <= 1) return null;
  const se = Math.sqrt(a.variance / a.effectiveBase + b.variance / b.effectiveBase);
  return se > 0 ? (a.mean - b.mean) / se : null;
}

function directionOf(z: number | null, level: 90 | 95 | 99): Direction {
  if (z === null || Math.abs(z) < Z_CRITICAL[level]) return null;
  return z > 0 ? 'higher' : 'lower';
}

export function compareProportions(
  p1: number, n1: number, p2: number, n2: number, level: 90 | 95 | 99,
): Direction {
  return directionOf(proportionZ(p1, n1, p2, n2), level);
}

export function compareMeans(a: MeanStats, b: MeanStats, level: 90 | 95 | 99): Direction {
  return directionOf(meanZ(a, b), level);
}

/** The two-tailed p-value of a z statistic. */
export function twoTailedP(z: number): number {
  return 2 * (1 - normalCdf(Math.abs(z)));
}

/**
 * The standard normal CDF, through Abramowitz and Stegun's 7.1.26 approximation of erf -- accurate to
 * about 1e-7, several orders finer than any confidence level a table is tested at.
 */
function normalCdf(z: number): number {
  const x = Math.abs(z) / Math.SQRT2;
  const t = 1 / (1 + 0.3275911 * x);
  const poly = t * (0.254829592 + t * (-0.284496736 + t * (1.421413741 + t * (-1.453152027 + t * 1.061405429))));
  const erf = 1 - poly * Math.exp(-x * x);
  return 0.5 * (1 + (z < 0 ? -erf : erf));
}

export type Correction = 'none' | 'bonferroni' | 'bh';

export const CORRECTION_NAMES: Record<Correction, string> = {
  none: 'no correction for multiple comparisons',
  bonferroni: 'Bonferroni-corrected within each row',
  bh: 'Benjamini-Hochberg-corrected across the table',
};

/**
 * Which of a table's tests stay significant once corrected for how many were run. `tests` are
 * p-values grouped by row; the answer is a set of `row:index` keys that pass.
 *
 * Bonferroni divides the threshold by the number of tests in the row -- strict, and the usual choice
 * where every comparison in a row is read as one claim. Benjamini-Hochberg controls the share of
 * false findings across the whole table, which keeps more of a large table's real differences.
 */
export function significantTests(
  tests: readonly (readonly number[])[],
  level: 90 | 95 | 99,
  correction: Correction,
): Set<string> {
  const alpha = 1 - level / 100;
  const passed = new Set<string>();

  if (correction === 'bh') {
    const all = tests.flatMap((row, r) => row.map((p, i) => ({ key: `${r}:${i}`, p })))
      .sort((a, b) => a.p - b.p);
    let cutoff = -1;
    all.forEach((test, rank) => {
      if (test.p <= ((rank + 1) / all.length) * alpha) cutoff = rank;
    });
    all.slice(0, cutoff + 1).forEach(test => passed.add(test.key));
    return passed;
  }

  tests.forEach((row, r) => {
    const threshold = correction === 'bonferroni' && row.length ? alpha / row.length : alpha;
    row.forEach((p, i) => { if (p < threshold) passed.add(`${r}:${i}`); });
  });
  return passed;
}

/** A weighted mean, with what a test needs alongside it. */
export interface MeanStats {
  mean: number;
  variance: number;
  /** Weights summed. */
  sumWeights: number;
  /** Weights squared and summed: together with `sumWeights`, what gives the effective base. */
  sumSquares: number;
  effectiveBase: number;
  /** Respondents averaged, unweighted. */
  count: number;
}

export function meanStats(values: readonly { value: number; weight: number }[]): MeanStats | null {
  let sumWeights = 0;
  let sumSquares = 0;
  let total = 0;
  let count = 0;
  for (const { value, weight } of values) {
    if (!(weight > 0)) continue;
    count++;
    sumWeights += weight;
    sumSquares += weight * weight;
    total += value * weight;
  }
  if (!(sumWeights > 0)) return null;

  const mean = total / sumWeights;
  let spread = 0;
  for (const { value, weight } of values) {
    if (weight > 0) spread += weight * (value - mean) ** 2;
  }
  return {
    mean,
    // The population form over the weights; for an unweighted column of thirty or more it differs
    // from the sample form by under 4%, and a test on fewer is not run.
    variance: spread / sumWeights,
    sumWeights,
    sumSquares,
    effectiveBase: effectiveBase(sumWeights, sumSquares),
    count,
  };
}

/**
 * Kish's effective sample size, (Σw)² / Σw². Equal to the head count when every weight is 1, and
 * smaller the more the weights vary -- which is the precision weighting costs.
 */
export function effectiveBase(sumWeights: number, sumSquares: number): number {
  return sumSquares > 0 ? (sumWeights * sumWeights) / sumSquares : 0;
}

/**
 * The letter a column is known by in a tested table: A-Z, then AA, AB... A table that needs the
 * second round has bigger problems, but it still gets distinct letters rather than repeats.
 */
export function columnLetter(index: number): string {
  let letter = '';
  let n = index;
  do {
    letter = String.fromCharCode(65 + (n % 26)) + letter;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return letter;
}

/** A respondent's weight from its cell. Missing, unreadable, zero or negative weighs nothing. */
export function weightValue(value: unknown): number {
  if (value === null || value === undefined || Array.isArray(value)) return 0;
  const parsed = Number.parseFloat(String(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/** A column that looks like it holds respondent weights, with what the panel says about it. */
export interface WeightCandidate {
  column: string;
  /** Respondents with a usable weight. */
  weighted: number;
  total: number;
  mean: number;
  min: number;
  max: number;
  /** Named like a weight, which ranks it first. */
  named: boolean;
}

const WEIGHT_NAME = /weight|wgt|(^|[_\W])wt|wt($|[_\W\d])/i;

/** How far from 1 a column's mean can sit and still read as weights. Weights usually average 1. */
const WEIGHT_MEAN_RANGE = { min: 0.2, max: 5 };

/** Below this share of respondents carrying a number, a column is something other than a weight. */
const WEIGHT_COVERAGE = 0.9;

/**
 * Columns in the data that could be respondent weights. Read off the records rather than the
 * datamap: a weight is a single number per respondent, which the layout deliberately leaves out, and
 * one merged in after fielding may not be in the datamap at all.
 *
 * A column qualifies when nearly everyone has a positive number in it, the numbers vary, and they
 * average somewhere near 1. One named like a weight is listed whatever its mean, since a weight
 * scaled to population totals averages in the thousands.
 */
export function weightCandidates(
  records: readonly Record<string, unknown>[],
  excluded: ReadonlySet<string> = new Set(),
): WeightCandidate[] {
  if (!records.length) return [];

  const columns = new Set<string>();
  for (const record of records) {
    for (const key of Object.keys(record)) columns.add(key);
  }

  const found: WeightCandidate[] = [];
  for (const column of columns) {
    if (excluded.has(column)) continue;
    let weighted = 0;
    let sum = 0;
    let min = Number.POSITIVE_INFINITY;
    let max = 0;
    for (const record of records) {
      const weight = weightValue(record[column]);
      if (!weight) continue;
      weighted++;
      sum += weight;
      min = Math.min(min, weight);
      max = Math.max(max, weight);
    }
    if (weighted < records.length * WEIGHT_COVERAGE || min === max) continue;

    const mean = sum / weighted;
    const named = WEIGHT_NAME.test(column);
    const nearOne = mean >= WEIGHT_MEAN_RANGE.min && mean <= WEIGHT_MEAN_RANGE.max;
    if (!named && !nearOne) continue;
    found.push({ column, weighted, total: records.length, mean, min, max, named });
  }

  return found.sort((a, b) =>
    Number(b.named) - Number(a.named) || a.column.localeCompare(b.column));
}
